'use client';

import React, { useCallback, useEffect, useState } from 'react';
import ResponsiveModal from 'src/components/Modals/ResponsiveModal/ResponsiveModal';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import { useCurrentAccountMarginInfo } from 'src/hooks/query/accountTradingData/useMarginInfo';
import {
	BASE_PRECISION_EXP,
	BigNum,
	MarginCategory,
	PERCENTAGE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
	ZERO,
} from '@velocity-exchange/sdk';
import useDriftClient from 'src/hooks/useDriftClient';
import useDriftClientIsReady from 'src/hooks/useDriftClientIsReady';
import useUserAccountIsReady from 'src/hooks/useUserAccountIsReady';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import { toNotional } from 'src/utils/formatting';
import MarketIcon from './Utils/MarketIcon';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { ChevronDown, ChevronUp, Heart } from '@velocity-exchange/icons';
import useAccountExists from 'src/hooks/useAccountExists';
import CustomMaxLeverageCta from './CustomMaxLeverageCta';
import { useAccountsBeingLiquidatedWithoutBankruptcies } from 'src/hooks/Liquidations/useAccountsBeingLiquidated';
import InlineAlert from 'src/components/primitives/InlineAlert/InlineAlert';
import TextButton from 'src/components/primitives/TextButton/TextButton';
import {
	BaseAssetSymbol,
	MarketDisplaySymbol,
	UIMarket,
} from '@velocity-exchange/common';
import useModal from 'src/components/Modals/hooks/useModal';
import { cj } from 'src/utils/classNames';
import Button from 'src/components/primitives/Button/Button';
import healthDependentClassName from 'src/utils/healthDependentClassName';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';

const WEIGHT_PRECISION_EXP = 4;

const FooterRow = React.memo(function FooterRowMemo({
	totalValue,
	type,
	marginCategory,
}: {
	totalValue: string;
	type: 'asset' | 'liability';
	marginCategory: MarginCategory;
}) {
	return (
		<div
			className={cj(
				'flex items-center justify-end gap-x-sm',
				'-mx-lg rounded-b-sm bg-main-bg px-lg py-sm'
			)}
		>
			<span className="typo-body-xs text-text-secondary">
				{marginCategory === 'Initial'
					? 'Total'
					: type === 'asset'
						? 'Total Margin Collateral'
						: 'Total Maintenance Margin'}
			</span>
			<span className="typo-body-xs text-text-default">{totalValue}</span>
		</div>
	);
});

const HeaderRow = React.memo(function HeaderRowMemo({
	firstColumnLabel,
	isMobile,
}: {
	firstColumnLabel: string;
	isMobile: boolean;
}) {
	return (
		<div
			className={cj(
				'-mx-lg mb-2xs grid grid-flow-col items-end px-lg py-sm typo-body-xs text-text-secondary',
				'grid-cols-health-modal-mobile border-b border-stroke-divider sm:grid-cols-health-modal'
			)}
		>
			<div>{firstColumnLabel}</div>
			{isMobile ? (
				<>
					<div className="text-right">Weight</div>
					<div className="text-right">Weighted Value</div>
				</>
			) : (
				<>
					<div>Value / Size</div>
					<div className="text-right">Weighted Value / Weight</div>
				</>
			)}
		</div>
	);
});

const AssetLiabilityRow = React.memo(function AssetLiabilityRowMemo(
	props: AssetLiabilityRowData & { isMobile: boolean }
) {
	return (
		<div
			className={cj(
				'-mx-lg grid grid-flow-col items-center px-lg py-sm',
				'grid-cols-health-modal-mobile sm:grid-cols-health-modal'
			)}
		>
			<div className="flex items-center gap-x-sm text-text-default">
				<MarketIcon marketSymbol={props.marketSymbol} />{' '}
				<span className="typo-label-xs">{props.label}</span>
			</div>
			{!props.isMobile && (
				<div>
					<span className="typo-label-xs text-text-default">{props.value}</span>
					{!props.isPnl && (
						<>
							<br />
							<span className="typo-body-xs text-text-secondary">
								{props.size} {props.baseAssetSymbol}
							</span>
						</>
					)}
				</div>
			)}
			{props.isMobile ? (
				<>
					<div className="text-right">
						<span className="typo-label-xs text-text-default">
							{props.weight}%
						</span>
					</div>
					<div className="text-right">
						<span className="typo-body-xs text-text-default">
							{props.weightedValue}
						</span>
					</div>
				</>
			) : (
				<div className="w-full text-right">
					<span className="typo-label-xs text-text-default">
						{props.weightedValue}
					</span>
					<br />
					<span className="typo-body-xs text-text-secondary">
						{props.weight}%
					</span>
				</div>
			)}
		</div>
	);
});

const UPnlSummaryRow = React.memo(function UPnlSummaryRowMemo({
	averageWeight,
	totalWeightedValue,
	totalValue,
	expanded,
	onClick,
	isMobile,
}: {
	averageWeight: number;
	totalWeightedValue: number;
	totalValue: number;
	expanded: boolean;
	onClick: () => void;
	isMobile: boolean;
}) {
	return (
		<div
			className={cj(
				'-mx-lg grid grid-flow-col items-center border-b px-lg py-sm text-text-default transition-colors',
				'grid-cols-health-modal-mobile sm:grid-cols-health-modal',
				expanded ? 'border-stroke-divider' : 'border-transparent'
			)}
		>
			<button
				className="flex items-center gap-x-xs typo-body-xs"
				onClick={onClick}
			>
				Unsettled
				{expanded ? <ChevronUp /> : <ChevronDown />}
			</button>
			{!isMobile && (
				<span className="typo-label-xs">{toNotional(totalValue)}</span>
			)}
			{isMobile ? (
				<>
					<span className="text-right typo-label-xs text-text-default">
						{averageWeight}%
					</span>
					<span className="text-right typo-body-xs">
						{toNotional(totalWeightedValue)}
					</span>
				</>
			) : (
				<div className="text-right">
					<span className="typo-label-xs">
						{toNotional(totalWeightedValue)}
					</span>
					<br />
					<span className="typo-body-xs text-text-secondary">
						{averageWeight}%
					</span>
				</div>
			)}
		</div>
	);
});

type AssetLiabilityRowData = {
	marketSymbol: MarketDisplaySymbol;
	baseAssetSymbol: BaseAssetSymbol;
	label: string;
	weight: number | string;
	weightedValue: string;
	value: string;
	size: string;
	isPnl?: boolean;
};

interface AccountHealthModalContentProps {
	marginCategory: MarginCategory;
}

const AccountHealthModalContent = ({
	marginCategory,
}: AccountHealthModalContentProps) => {
	const { show } = useModal();
	const isMobile = useIsMobileScreenSize();
	const [isAssetUPnlExpanded, setIsAssetUPnlExpanded] = useState(false);
	const [showLiquidationNotice, setShowLiquidationNotice] = useState(false);
	const accountExists = useAccountExists();
	const connected = useWalletIsConnected();
	const userAccountIsReady = useUserAccountIsReady();
	const marginInfo = useCurrentAccountMarginInfo();

	const accountKey = useDriftAccountStore((s) =>
		s.currentUserKey ? s.accounts[s.currentUserKey]?.userKey : undefined
	);

	const driftClient = useDriftClient();
	const driftClientIsReady = useDriftClientIsReady();
	const user =
		connected &&
		accountExists &&
		driftClient &&
		driftClientIsReady &&
		driftClient.hasUser()
			? driftClient.getUser()
			: undefined;

	const accountsBeingLiqd = useAccountsBeingLiquidatedWithoutBankruptcies();

	const health = connected && userAccountIsReady && user?.getHealth();
	const healthDisplay =
		typeof health === 'number' && !isNaN(health) ? health : 100;

	const freeCollateralBN =
		marginCategory === 'Initial'
			? (marginInfo?.freeCollateral ?? ZERO)
			: (marginInfo?.maintenanceFreeCollateral ?? ZERO);

	const freeCollateral = toNotional(
		BigNum.from(freeCollateralBN, QUOTE_PRECISION_EXP).toNum()
	);

	let totalAssetWeight = 0;
	let totalLiabilityWeight = 0;

	let assetUPnlAvgWeight = 0;
	let assetUPnlTotalValue = 0;
	let assetUPnlTotalWeightedValue = 0;

	const assets: AssetLiabilityRowData[] = [];
	const uPnlAssets: AssetLiabilityRowData[] = [];
	const liabilities: AssetLiabilityRowData[] = [];

	const { borrows, deposits, perpPositions, perpPnl } =
		user?.getHealthComponents({ marginCategory }) || {};

	deposits?.forEach((deposit) => {
		const spotMarketUiConfig = UIMarket.createSpotMarket(deposit.marketIndex);

		const weight = BigNum.from(deposit.weight, WEIGHT_PRECISION_EXP);
		const weightedValue = BigNum.from(
			deposit.weightedValue,
			QUOTE_PRECISION_EXP
		);
		const value = BigNum.from(deposit.value, QUOTE_PRECISION_EXP);
		const size = BigNum.from(deposit.size, spotMarketUiConfig.precisionExp);

		totalAssetWeight += weightedValue.toNum();

		assets.push({
			marketSymbol: spotMarketUiConfig.uiSymbols.marketDisplaySymbol,
			baseAssetSymbol: spotMarketUiConfig.uiSymbols.baseAssetSymbol,
			label: spotMarketUiConfig.uiSymbols.marketDisplaySymbol,
			weight: parseFloat((weight.toNum() * 100).toFixed(2)),
			weightedValue: weightedValue.toNotional(),
			value: value.toNotional(),
			size: size.prettyPrint(true),
			isPnl: false,
		});
	});

	borrows?.forEach((borrow) => {
		const spotMarketUiConfig = UIMarket.createSpotMarket(borrow.marketIndex);

		const weight = BigNum.from(borrow.weight, WEIGHT_PRECISION_EXP);
		const weightedValue = BigNum.from(
			borrow.weightedValue,
			QUOTE_PRECISION_EXP
		);
		const value = BigNum.from(borrow.value, QUOTE_PRECISION_EXP);
		const size = BigNum.from(borrow.size, spotMarketUiConfig.precisionExp);

		totalLiabilityWeight += weightedValue.toNum();

		liabilities.push({
			marketSymbol: spotMarketUiConfig.uiSymbols.marketDisplaySymbol,
			baseAssetSymbol: spotMarketUiConfig.uiSymbols.baseAssetSymbol,
			label: `${spotMarketUiConfig.uiSymbols.marketDisplaySymbol} borrow`,
			weight: BigNum.from(
				weight.toNum() * 100 * 10 ** PERCENTAGE_PRECISION_EXP.toNumber(),
				PERCENTAGE_PRECISION_EXP
			).prettyPrint(),
			weightedValue: weightedValue.toNotional(),
			value: value.toNotional(),
			size: size.prettyPrint(true),
			isPnl: false,
		});
	});

	perpPositions?.forEach((position) => {
		const perpPosition = user?.getPerpPosition(position.marketIndex);
		if (perpPosition && user?.isPerpPositionIsolated(perpPosition)) {
			return;
		}
		const perpMarketUiConfig = UIMarket.createPerpMarket(position.marketIndex);

		const weight = BigNum.from(position.weight, WEIGHT_PRECISION_EXP);
		const weightedValue = BigNum.from(
			position.weightedValue,
			QUOTE_PRECISION_EXP
		);
		const value = BigNum.from(position.value, QUOTE_PRECISION_EXP);
		const size = BigNum.from(position.size, BASE_PRECISION_EXP);

		totalLiabilityWeight += weightedValue.toNum();

		liabilities.push({
			marketSymbol: perpMarketUiConfig.uiSymbols.marketDisplaySymbol,
			baseAssetSymbol: perpMarketUiConfig.uiSymbols.baseAssetSymbol,
			label: `${perpMarketUiConfig.uiSymbols.marketDisplaySymbol}`,
			weight: BigNum.from(
				weight.toNum() * 100 * 10 ** PERCENTAGE_PRECISION_EXP.toNumber(),
				PERCENTAGE_PRECISION_EXP
			).prettyPrint(),
			weightedValue: weightedValue.toNotional(),
			value: value.toNotional(),
			size: size.prettyPrint(true),
			isPnl: false,
		});
	});

	perpPnl?.forEach((pnl) => {
		// Skip isolated positions - their unsettled P&L doesn't transfer to the cross account
		const perpPosition = user?.getPerpPosition(pnl.marketIndex);
		if (perpPosition && user?.isPerpPositionIsolated(perpPosition)) {
			return;
		}

		const perpMarketUiConfig = UIMarket.createPerpMarket(pnl.marketIndex);

		const weight = Math.round(
			BigNum.from(pnl.weight, WEIGHT_PRECISION_EXP).toNum() * 100
		);
		const weightedValue = BigNum.from(pnl.weightedValue, QUOTE_PRECISION_EXP);
		const value = BigNum.from(pnl.value, QUOTE_PRECISION_EXP);
		const size = BigNum.from(pnl.size, BASE_PRECISION_EXP);

		assetUPnlAvgWeight += weight;
		assetUPnlTotalValue += value.toNum();
		totalAssetWeight += weightedValue.toNum();
		assetUPnlTotalWeightedValue += weightedValue.toNum();

		uPnlAssets.push({
			marketSymbol: perpMarketUiConfig.uiSymbols.marketDisplaySymbol,
			baseAssetSymbol: perpMarketUiConfig.uiSymbols.baseAssetSymbol,
			label: `${perpMarketUiConfig.uiSymbols.marketDisplaySymbol}`,
			weight,
			weightedValue: weightedValue.toNotional(),
			value: value.toNotional(),
			size: size.toTradePrecision(),
			isPnl: true,
		});
	});

	assetUPnlAvgWeight = uPnlAssets.length
		? Math.round(assetUPnlAvgWeight / uPnlAssets.length)
		: 0;

	const handleExpandAssetPnl = useCallback(() => {
		setIsAssetUPnlExpanded(!isAssetUPnlExpanded);
	}, [isAssetUPnlExpanded]);

	useEffect(() => {
		if (accountsBeingLiqd?.length > 0) {
			if (
				accountsBeingLiqd
					.map((acc) => acc.userKey)
					.some((accKey) => accKey === accountKey)
			) {
				setShowLiquidationNotice(true);
			} else {
				setShowLiquidationNotice(false);
			}
		} else {
			setShowLiquidationNotice(false);
		}
	}, [accountsBeingLiqd, accountKey]);

	return (
		<>
			<ResponsiveModal.Body>
				<div className="flex flex-col gap-y-sm">
					{showLiquidationNotice && (
						<InlineAlert
							variant="error"
							title="This account is in liquidation state; it can be liquidated more unless funds are deposited. Trades and withdrawals are disabled."
						>
							<TextButton
								size="sm"
								onClick={() =>
									show({
										type: 'deposit',
										props: { targetAccountKey: accountKey },
									})
								}
							>
								Click here to deposit
							</TextButton>
						</InlineAlert>
					)}
					<CustomMaxLeverageCta />
					{marginCategory === 'Maintenance' ? (
						<>
							<DefinitionTooltip
								definitionId="tradingAccountHealth"
								className="w-fit typo-body-xs text-text-default"
							>
								Account Health
							</DefinitionTooltip>
							<span
								className={cj(
									'flex items-center gap-xs typo-heading-h4',
									healthDependentClassName(healthDisplay, { type: 'text' })
								)}
							>
								<Heart size={24} />
								{healthDisplay}%
							</span>
						</>
					) : (
						<>
							<span className="typo-body-xs text-text-default">
								Remaining Available Collateral
							</span>
							<span className="typo-heading-h4 text-text-positive-green-button">
								{freeCollateral}
							</span>
						</>
					)}
					<span className="typo-body-xs text-text-secondary">
						{marginCategory === 'Initial'
							? 'Initial margin represents the amount used to trade or borrow.'
							: 'Your positions will be liquidated if Account Health reaches 0%. Isolated margin perp positions do not count here.'}
					</span>
				</div>

				<div className="w-full rounded-sm border border-stroke-divider px-lg">
					<HeaderRow firstColumnLabel="Deposit (Asset)" isMobile={isMobile} />

					{assets.length > 0 || uPnlAssets.length > 0 ? (
						<>
							{assets.map((data) => (
								<AssetLiabilityRow
									key={data.label}
									isMobile={isMobile}
									{...data}
								/>
							))}
							{uPnlAssets.length > 0 && (
								<>
									<UPnlSummaryRow
										averageWeight={assetUPnlAvgWeight}
										totalWeightedValue={assetUPnlTotalWeightedValue}
										totalValue={assetUPnlTotalValue}
										expanded={isAssetUPnlExpanded}
										onClick={handleExpandAssetPnl}
										isMobile={isMobile}
									/>
									{isAssetUPnlExpanded &&
										uPnlAssets.map((data) => (
											<AssetLiabilityRow
												key={data.label}
												isMobile={isMobile}
												{...data}
											/>
										))}
								</>
							)}
							<FooterRow
								totalValue={toNotional(totalAssetWeight)}
								type="asset"
								marginCategory={marginCategory}
							/>
						</>
					) : (
						<div className="w-full p-2xl text-center typo-body-xs">
							No Assets
						</div>
					)}
				</div>

				<div className="w-full rounded-sm border border-stroke-divider px-lg">
					<HeaderRow
						firstColumnLabel="Position (Liability)"
						isMobile={isMobile}
					/>

					{liabilities.length > 0 ? (
						<>
							{liabilities.map((data) => (
								<AssetLiabilityRow
									key={data.label}
									isMobile={isMobile}
									{...data}
								/>
							))}
							<FooterRow
								totalValue={toNotional(totalLiabilityWeight)}
								type="liability"
								marginCategory={marginCategory}
							/>
						</>
					) : (
						<div className="w-full p-2xl text-center typo-body-xs">
							No Liabilities
						</div>
					)}
				</div>
			</ResponsiveModal.Body>
			<ResponsiveModal.Footer>
				{({ close }) => (
					<Button size="lg" onClick={close} className="w-full">
						Close
					</Button>
				)}
			</ResponsiveModal.Footer>
		</>
	);
};

export default React.memo(AccountHealthModalContent);
